import React from "react";
import '../../css/modals-history.css'

/* ============================================================
   CdsxHistoryModal — Historial de eventos del cliente en la mesa.
   Componente independiente y exportable. Línea cds-x-*.

   ── Estructura de datos (diseñada; reemplaza con tu backend) ──
   events: Array<{
     id: string | number,
     type: "enter" | "order" | "update" | "pay-request" | "paid" | "pending",
     title: string,           // "Realizó un pedido"
     participant?: string,    // quién hizo la acción (nombre del cliente)
     description?: string,    // detalle opcional
     time?: string,           // "7:02 p.m." (ya formateado) o null
     amount?: number,         // monto opcional (se muestra $)
     badge?: { label: string, kind: "paid" | "pending" | "request" }
   }>

   Props:
     open        bool         — controla visibilidad
     onClose     () => void
     table       { number, zone_name?, ... }     events      array (arriba). Si va vacío muestra "Sin eventos registrados".
   ============================================================ */

const IcoByType = {
    "enter": "→",
    "order": "🍽",
    "update": "✎",
    "pay-request": "⏱",
    "paid": "✓",
    "pending": "$",
    "discount": "%",
};

export default function CdsxHistoryModal({ open, onClose, table, events, onChargePosPayment, onReviewDiscounts }) {
    if (!open) return null;

    // "Pidió pagar en el POS": mientras la solicitud siga pendiente en la mesa, tocar el evento abre
    // el mismo cobro en terminal que el botón "Cobrar en terminal" del detalle de la mesa. Las
    // solicitudes ya resueltas (o vencidas) quedan como historial normal, sin acción.
    // Una mesa con dos pedidos puede tener varias solicitudes pendientes: cada evento cobra la suya.
    const pendingRequests = Array.isArray(table?.posPaymentRequests) && table.posPaymentRequests.length > 0
        ? table.posPaymentRequests
        : (table?.posPaymentRequest ? [table.posPaymentRequest] : []);
    const requestFor = (ev) => pendingRequests.find((request) => request.payment_attempt_id === ev.payment_attempt_id) || null;
    const isChargeable = (ev) => Boolean(onChargePosPayment && ev.payment_attempt_id && requestFor(ev));
    // "Solicitó un descuento": abre la revision de la solicitud mientras siga pendiente (ningun evento posterior
    // de descuento sobre la misma linea - aprobado, rechazado o cancelado).
    const handleCharge = (ev) => {
        onClose?.();
        onChargePosPayment?.(table, requestFor(ev));
    };

    const chronological = Array.isArray(events) ? events : [];
    const isDiscountPending = (ev) => Boolean(
        onReviewDiscounts && ev.type === "discount" && ev.badge?.kind === "request" && ev.line_uuid
    ) && !chronological.some((other) => (
        other.type === "discount"
        && other.line_uuid === ev.line_uuid
        && chronological.indexOf(other) > chronological.indexOf(ev)
        && other.badge?.kind !== "request"
    ));
    const handleReviewDiscount = () => onReviewDiscounts?.(table);

    // Accion de un evento (si la tiene): solo el mas reciente de cada solicitud aun abierta.
    const actionFor = (ev) => {
        if (isChargeable(ev)) return { handler: () => handleCharge(ev), cta: "Toca para cobrar en terminal", icon: "bi-credit-card-2-front" };
        if (isDiscountPending(ev)) return { handler: handleReviewDiscount, cta: "Toca para revisar el descuento", icon: "bi-percent" };
        return null;
    };


    // Los eventos llegan en orden cronológico; el modal muestra el más reciente primero.
    const list = Array.isArray(events) ? [...events].reverse() : [];

    return (
        <div className="cds-x-ovl" onClick={onClose}>
            <div className="cds-x-mdl" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
                <div className="cds-x-mdl-head">
                    <div>
                        <div className="cds-x-mdl-title">Historial · Mesa {table?.number}</div>
                        {table?.zone_name ? <div className="cds-x-mdl-sub">{table.zone_name}</div> : null}
                    </div>
                    <button className="cds-x-mdl-close" type="button" onClick={onClose} aria-label="Cerrar">
                        <i className="bi bi-x-lg" />
                    </button>
                </div>

                <div className="cds-x-mdl-body">
                    {list.length === 0 ? (
                        <div className="cds-x-mdl-empty">Sin eventos registrados</div>
                    ) : (
                        <div className="cds-x-tl">
                            {list.map((ev) => {
                                const action = actionFor(ev);
                                return (
                                <div className="cds-x-tl-item" key={ev.id}>
                                    <div className="cds-x-tl-rail">
                                        <div className={`cds-x-tl-dot ${ev.type}`}>{IcoByType[ev.type] || "•"}</div>
                                        <div className="cds-x-tl-line" />
                                    </div>
                                    <div
                                        className={`cds-x-tl-body${action ? " is-action" : ""}`}
                                        {...(action
                                            ? {
                                                role: "button",
                                                tabIndex: 0,
                                                onClick: action.handler,
                                                onKeyDown: (e) => { if (e.key === "Enter") action.handler(); },
                                            }
                                            : {})}
                                    >
                                        {ev.participant ? (
                                            <div className="cds-x-tl-who">
                                                <i className="bi bi-person-circle" /> {ev.participant} 
                                            </div>
                                        ) : null}
                                        <div className="cds-x-tl-top">
                                            <div className="cds-x-tl-title">{ev.title}</div>
                                            <div className="cds-x-tl-time">{ev.time || "—"}</div>
                                        </div>

                                        {ev.description ? <div className="cds-x-tl-desc">{ev.description}</div> : null}
                                        {typeof ev.amount === "number" ? (
                                            <div className="cds-x-tl-amount">${ev.amount.toFixed(2)}</div>
                                        ) : null}
                                        {ev.badge ? (
                                            <span className={`cds-x-tl-badge ${ev.badge.kind}`}>{ev.badge.label}</span>
                                        ) : null}
                                        {action ? (
                                            <div className="cds-x-tl-cta">
                                                <i className={`bi ${action.icon}`} /> {action.cta}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="cds-x-mdl-foot">
                    <button className="cds-x-mbtn ghost" type="button" onClick={onClose}>Cerrar</button>
                </div>
            </div>
        </div>
    );
}